import React, { startTransition } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  Box,
  useTheme,
  useMediaQuery,
  Typography,
  Tooltip,
  IconButton,
  Button,
  alpha
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Inventory as InventoryIcon,
  ShoppingCart as SalesIcon,
  People as CustomersIcon,
  Analytics as AnalyticsIcon,
  CardMembership as SubscriptionIcon,
  Settings as SettingsIcon,
  Payment as PaymentIcon,
  Receipt as InvoiceIcon,
  LocalShipping as SupplierIcon,
  LocalOffer as PromotionIcon,
  BuildCircle as RepairsIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Warehouse as WarehouseIcon,
} from '@mui/icons-material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Link } from 'react-router-dom';
import { useBusiness } from '../../context/BusinessContext';

const menuGroups = [
  {
    group: 'Inicio',
    items: [
      { text: 'Resumen Diario', icon: <DashboardIcon />, path: '/dashboard' },
    ],
  },
  {
    group: 'Tu Negocio',
    items: [
      { text: 'Inventario Pro', icon: <WarehouseIcon />, path: '/inventory' },
      { text: 'Mis Productos', icon: <InventoryIcon />, path: '/products' },
      { text: 'Ventas y Caja', icon: <SalesIcon />, path: '/sales' },
      { text: 'Reparaciones', icon: <RepairsIcon />, path: '/reparaciones' },
    ],
  },
  {
    group: 'Relaciones',
    items: [
      { text: 'Mis Clientes', icon: <CustomersIcon />, path: '/customers' },
      { text: 'Análisis Pro', icon: <AnalyticsIcon />, path: '/analytics' },
    ],
  },
  {
    group: 'Operaciones',
    items: [
      { text: 'Facturación', icon: <InvoiceIcon />, path: '/billing' },
      { text: 'Proveedores', icon: <SupplierIcon />, path: '/suppliers' },
      { text: 'Promociones', icon: <PromotionIcon />, path: '/promotions' },
    ],
  },
  {
    group: 'Sistema',
    items: [
      { text: 'Suscripción', icon: <SubscriptionIcon />, path: '/subscriptions' },
      { text: 'Ajustes', icon: <SettingsIcon />, path: '/settings' },
    ],
  },
];

export const Navigation = ({
  mobileOpen,
  onClose,
  isAppMode = false,
  isCollapsed = false,
  onToggleCollapse,
  drawerWidth = 260
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { businessData } = useBusiness();

  const effectiveCollapsed = !isMobile && !isAppMode && isCollapsed;
  const currentWidth = isAppMode ? 0 : (effectiveCollapsed ? 72 : drawerWidth);

  const handleNavigation = (path) => {
    if (location.pathname !== path) {
      startTransition(() => {
        navigate(path);
      });
    }
    if ((isMobile || isAppMode) && onClose) {
      onClose();
    }
  };

  const drawerContent = (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      bgcolor: alpha(theme.palette.background.paper, 0.95),
      backdropFilter: 'blur(10px)',
      overflowX: 'hidden',
    }}>
      {/* Brand Section */}
      <Box
        sx={{
          p: effectiveCollapsed ? 1.5 : 2.5,
          pt: effectiveCollapsed ? 2.5 : 3.5,
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.8,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.05)}`,
          mb: 1.5,
        }}
      >
        <Tooltip title="POSENT PRO · Sistema de Control" placement="right" disableHoverListener={!effectiveCollapsed} arrow>
          <Box
            component="img"
            src={businessData?.logo || "/Posent-logo.png"}
            alt="POSENT Logo"
            sx={{
              height: effectiveCollapsed ? 36 : 46,
              width: 'auto',
              filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))',
              transition: 'all 0.25s ease',
            }}
          />
        </Tooltip>
        {!effectiveCollapsed && (
          <>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontSize: '1.15rem',
                letterSpacing: -0.5,
                lineHeight: 1.2,
              }}
            >
              POSENT PRO
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.disabled', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1.5, fontSize: '0.58rem' }}>
              Sistema de Control
            </Typography>
          </>
        )}
      </Box>

      {/* Menu Groups */}
      <Box sx={{ overflowY: 'auto', overflowX: 'hidden', flex: 1, px: effectiveCollapsed ? 1 : 1.8, '&::-webkit-scrollbar': { width: 4 } }}>
        {menuGroups.map((group, idx) => (
          <Box key={group.group} sx={{ mb: effectiveCollapsed ? 1.5 : 2.5 }}>
            {!effectiveCollapsed ? (
              <Typography
                variant="caption"
                sx={{
                  px: 1.5,
                  mb: 0.8,
                  display: 'block',
                  fontWeight: 800,
                  color: 'text.secondary',
                  fontSize: '0.62rem',
                  textTransform: 'uppercase',
                  letterSpacing: 1
                }}
              >
                {group.group}
              </Typography>
            ) : (
              idx > 0 && <Divider sx={{ my: 1, mx: 1, opacity: 0.3 }} />
            )}
            <List sx={{ p: 0 }}>
              {group.items.map((item) => {
                const active = location.pathname === item.path;
                const buttonContent = (
                  <ListItemButton
                    onClick={() => handleNavigation(item.path)}
                    sx={{
                      borderRadius: effectiveCollapsed ? '12px' : '14px',
                      py: effectiveCollapsed ? 1.1 : 1.15,
                      px: effectiveCollapsed ? 1 : 1.8,
                      justifyContent: effectiveCollapsed ? 'center' : 'flex-start',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      bgcolor: active ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                      color: active ? 'primary.main' : 'text.secondary',
                      position: 'relative',
                      minHeight: 44,
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.06),
                        color: 'primary.main',
                        '& .MuiListItemIcon-root': { color: 'primary.main', transform: 'scale(1.12)' }
                      },
                    }}
                  >
                    {active && (
                      <Box
                        sx={{
                          position: 'absolute',
                          left: 0,
                          width: 3.5,
                          height: effectiveCollapsed ? '60%' : '55%',
                          bgcolor: 'primary.main',
                          borderRadius: '0 4px 4px 0'
                        }}
                      />
                    )}
                    <ListItemIcon
                      sx={{
                        minWidth: effectiveCollapsed ? 'auto' : 38,
                        justifyContent: 'center',
                        color: active ? 'primary.main' : 'inherit',
                        transition: 'all 0.2s'
                      }}
                    >
                      {React.cloneElement(item.icon, { fontSize: 'small' })}
                    </ListItemIcon>
                    {!effectiveCollapsed && (
                      <ListItemText
                        primary={item.text}
                        primaryTypographyProps={{
                          fontSize: '0.86rem',
                          fontWeight: active ? 700 : 500,
                          letterSpacing: -0.2
                        }}
                      />
                    )}
                  </ListItemButton>
                );

                return (
                  <ListItem key={item.text} disablePadding sx={{ mb: 0.4 }}>
                    {effectiveCollapsed ? (
                      <Tooltip title={item.text} placement="right" arrow enterDelay={150}>
                        {buttonContent}
                      </Tooltip>
                    ) : (
                      buttonContent
                    )}
                  </ListItem>
                );
              })}
            </List>
          </Box>
        ))}
      </Box>

      {/* Footer Support Section */}
      <Box sx={{ p: effectiveCollapsed ? 1 : 2, pb: effectiveCollapsed ? 2 : 3, borderTop: `1px solid ${alpha(theme.palette.divider, 0.05)}` }}>
        {effectiveCollapsed ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Guía Amiga · ¿Necesitas ayuda?" placement="right" arrow>
              <Link to="/manual" style={{ textDecoration: 'none' }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '12px',
                    bgcolor: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    transition: 'all 0.2s',
                    '&:hover': { transform: 'scale(1.08)', boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.35)}` }
                  }}
                >
                  <HelpOutlineIcon fontSize="small" />
                </Box>
              </Link>
            </Tooltip>

            {onToggleCollapse && (
              <Tooltip title="Expandir menú lateral (Ctrl+B)" placement="right" arrow>
                <IconButton
                  size="small"
                  onClick={onToggleCollapse}
                  sx={{
                    color: 'text.secondary',
                    bgcolor: alpha(theme.palette.action.hover, 0.05),
                    borderRadius: '10px',
                    width: 36,
                    height: 36,
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.12), color: 'primary.main' }
                  }}
                >
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ) : (
          <>
            <Link to="/manual" style={{ textDecoration: 'none' }}>
              <Box
                sx={{
                  p: 1.8,
                  borderRadius: '16px',
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                  }
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '10px',
                    bgcolor: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    flexShrink: 0
                  }}
                >
                  <HelpOutlineIcon fontSize="small" />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.8rem' }}>
                    Guía Amiga
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
                    ¿Necesitas ayuda?
                  </Typography>
                </Box>
              </Box>
            </Link>

            {onToggleCollapse && (
              <Box sx={{ mt: 1.2, textAlign: 'center' }}>
                <Button
                  onClick={onToggleCollapse}
                  size="small"
                  startIcon={<ChevronLeftIcon fontSize="small" />}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: '0.74rem',
                    color: 'text.secondary',
                    borderRadius: '10px',
                    px: 1.5,
                    py: 0.5,
                    '&:hover': { bgcolor: alpha(theme.palette.action.hover, 0.08), color: 'primary.main' }
                  }}
                >
                  Colapsar menú (Ctrl+B)
                </Button>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );

  const isTemporary = isMobile || isAppMode;

  return (
    <Drawer
      variant={isTemporary ? "temporary" : "permanent"}
      open={isTemporary ? mobileOpen : true}
      onClose={onClose}
      sx={{
        width: currentWidth,
        flexShrink: 0,
        transition: 'width 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        '& .MuiDrawer-paper': {
          width: isTemporary ? drawerWidth : currentWidth,
          boxSizing: 'border-box',
          borderRight: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          maxWidth: '85vw',
          overflowX: 'hidden',
          transition: 'width 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
}; 